/* /cdn/shop/t/5/assets/cart--566ede15.js — the AMKA cart. One module, no framework, no build step.
 *
 * WHY IT EXISTS. On 2026-08-09 a four-grader panel found that every buy control on the
 * store was a <button> with no handler: 12 on the home page, 14 on the shop page, and no
 * cart code anywhere. The pages had passed several rounds of design review, because
 * design review LOOKS and nobody had clicked. This is the thing those buttons now call.
 *
 * DESIGN DECISIONS, and the reason for each:
 *
 *  - VANILLA, deliberately. This behaviour has to survive being ported into a Shopify
 *    theme, where a framework would have to be unpicked first. Plain functions and one
 *    storage key port in an afternoon.
 *
 *  - STORAGE IS THE TRUTH, always re-read. Every total is computed from what is actually
 *    in localStorage at that moment, never from an in-memory mirror that a second tab or
 *    a failed write could have made stale. Slower by microseconds, and it cannot silently
 *    disagree with itself.
 *
 *  - QUANTITY 0 REMOVES THE LINE. A stored zero is a line that renders as nothing, prices
 *    as nothing, and confuses every total downstream.
 *
 *  - THE CATALOGUE IS NEVER TRUSTED FROM THE CART. The cart stores id and quantity only.
 *    Name, price and image are read from products.json at render time, so a price change
 *    can never be shadowed by a stale copy sitting in someone's browser from last week.
 *
 *  - IT SURVIVES A CORRUPT KEY. Anything unparseable is treated as an empty cart rather
 *    than thrown, because a shopper with a broken cart should see an empty one, not a
 *    dead page.
 *
 * No payment. No checkout. Deliberately.
 */
(function (global) {
  "use strict";

  var KEY = "amka-cart-v1";
  var DATA = null;                 // the catalogue, once loaded
  var listeners = [];
  var loading = null;

  /* VARIANTS (added 2026-09-05, additive, nothing above changes shape).
     The cart stays {productId: qty}. A SECOND key holds, per product, the Shopify variant the
     shopper chose: {id, title, price}. With no choice recorded, the product's catalogue price
     is used exactly as before, so every page built before this line behaves the same.
     /cdn/shop/t/5/assets/variants--e99c4f20.json (pull_variants.py, public storefront JSON) maps product id to its live
     variants and is what turns a bag into a Shopify cart permalink. */
  var VKEY = "amka-cart-variants-v1";
  var VARIANTS = null;
  var SHOP = "";
  /* products.json carries no image for one product; the store's own cutout stands in */
  var IMAGE_FALLBACK = { "mate-cup-bombilla": "/cdn/shop/t/5/assets/mate-gourd--b92378c8.webp", "french-press": "/cdn/shop/t/5/assets/french-press--d96ea6f5.webp", "ceramic-cups": "/cdn/shop/t/5/assets/ceramic-cups--39f3d931.webp" };
  /* Shopify option titles carry em dashes; the bag shows a middle dot, the id stays untouched */
  function tidy(t) { return String(t || "").replace(/\s*\u2014\s*/g, " \u00b7 "); }

  /* MY OWN URL, captured NOW, at module-evaluation time, and never later.
     `document.currentScript` is only correct while a script is actually executing. The
     first version of this file read it lazily inside load(), which is called from
     /cdn/shop/t/5/assets/cart-ui--684a57e9.js during ITS execution, so currentScript pointed at /cdn/shop/t/5/assets/cart-ui--684a57e9.js and the
     catalogue URL came out as /store/cart-ui.js/data/products.json. It 404'd, every
     product resolved to null, and the cart quietly reported a subtotal of KSh 0 with
     every line marked "priced in store". Nothing threw. Capturing it here is the fix. */
  var SELF = (function () {
    if (document.currentScript && document.currentScript.src) return document.currentScript.src;
    var all = document.getElementsByTagName("script");
    for (var i = all.length - 1; i >= 0; i--) {
      if (/\/cart\.js(\?|$)/.test(all[i].src || "")) return all[i].src;
    }
    return "";
  })();
  /* the folder that CONTAINS store/, so data/ is a sibling of it */
  var BASE = "";
  var DATA_URL = BASE + "/cdn/shop/t/5/assets/products--945e9e79.json";

  /* ---- storage, and nothing else may touch it ------------------------------- */

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || "{}");
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
      var out = {};
      Object.keys(raw).forEach(function (id) {
        var n = parseInt(raw[id], 10);
        if (id && isFinite(n) && n > 0) out[id] = n;   /* a zero or a NaN is not a line */
      });
      return out;
    } catch (e) {
      return {};                    /* a broken cart is an empty cart, never an exception */
    }
  }

  function write(state) {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode, or the quota is full. The cart stops persisting and the page keeps
         working; announcing a failure the shopper cannot act on helps nobody. */
    }
    emit();
  }

  function emit() {
    var snap = { items: items(), count: count(), subtotal: subtotal() };
    listeners.forEach(function (fn) {
      try { fn(snap); } catch (e) {}
    });
  }

  /* ---- the catalogue --------------------------------------------------------- */

  /* A FAILED LOAD IS NOT A CATALOGUE.

     This resolved to {} on a 503 and then cached that {} in DATA, so every later call
     returned the empty catalogue instantly: no retry was possible and no caller could
     even see that it had failed. Root reproduced it exactly, one 503 and the page was
     dead for the rest of its life, "no product with id terroir" on every click.

     Now a failure REJECTS and caches nothing, so the next call is a real second fetch.
     Callers must attach a rejection handler; every one in this repo does. */
  function load() {
    if (DATA) return Promise.resolve(DATA);
    if (loading) return loading;
    loading = fetch(DATA_URL, { cache: "no-cache" })
      .then(function (r) {
        if (!r.ok) throw new Error("catalogue HTTP " + r.status + " at " + DATA_URL);
        return r.json();
      })
      .then(function (j) {
        var next = {};
        (j.products || []).forEach(function (p) { next[p.id] = p; });
        /* An EMPTY catalogue is a failure too: it is what a 200-with-nothing looks like,
           and it produced the KSh 0 subtotal that read as a design decision. */
        if (!Object.keys(next).length) throw new Error("catalogue arrived empty");
        return loadVariants().then(function () { DATA = next; return DATA; });
      })
      .catch(function (err) {
        loading = null;                    /* nothing cached: the next call refetches */
        console.error("[amka] catalogue failed to load:", err && err.message,
                      "— retrying is possible, prices are missing until it succeeds");
        throw err;
      });
    return loading;
  }

  function product(id) { return (DATA && DATA[id]) || null; }

  /* ---- variants (additive) --------------------------------------------------- */

  /* Variants are ADDITIVE: the products load can succeed without them, and purchasable()
     already treats "no snapshot" as available rather than sold out. So a failure here
     resolves, but it does NOT cache an empty snapshot, or one bad fetch would mark the
     whole shelf unknown for the rest of the session. */
  function loadVariants() {
    if (VARIANTS) return Promise.resolve(VARIANTS);
    if (global.AMKA_LIVE && global.AMKA_LIVE.products) { VARIANTS = global.AMKA_LIVE.products; return Promise.resolve(VARIANTS); }
    return fetch(BASE + "/cdn/shop/t/5/assets/variants--e99c4f20.json", { cache: "no-cache" })
      .then(function (r) {
        if (!r.ok) throw new Error("variants HTTP " + r.status);
        return r.json();
      })
      .then(function (j) { VARIANTS = j.products || {}; return VARIANTS; })
      .catch(function (err) {
        console.warn("[amka] variant snapshot unavailable, availability is unknown:",
                     err && err.message);
        return null;                       /* VARIANTS stays null, so nothing is "sold out" */
      });
  }

  function readV() {
    try {
      var v = JSON.parse(global.localStorage.getItem(VKEY) || "{}");
      return v && typeof v === "object" ? v : {};
    } catch (e) { return {}; }
  }

  function writeV(m) {
    try { global.localStorage.setItem(VKEY, JSON.stringify(m)); } catch (e) {}
    emit();
  }

  /* Record the chosen Shopify variant for a product. v = {id, title, price} or null. */
  function setVariant(id, v) {
    var m = readV();
    if (!v || !v.id) { delete m[id]; }
    else {
      var price = typeof v.price === "number" ? v.price : parseFloat(v.price);
      m[id] = { id: String(v.id), title: tidy(v.title) };
      if (isFinite(price)) m[id].price = price;
    }
    writeV(m);
    return m[id] || null;
  }

  function variantOf(id) { return readV()[id] || null; }

  /* A cart KEY is "<productId>" or "<productId>@<variantId>": two sizes of the same coffee are
     two lines (Astra review 2026-09-08, finding 1). pidOf() strips the variant part. */
  function pidOf(key) { return String(key).split("|")[0].split("@")[0]; }

  /* Purchasable = at least one live variant available in /cdn/shop/t/5/assets/variants--e99c4f20.json. Controls for a
     product that is not purchasable are disabled by the binder, wherever they sit. */
  function purchasable(id) {
    if (!VARIANTS) return true;              /* snapshot not loaded: the binder waits for load() first */
    var p = VARIANTS[pidOf(id)];
    if (!p || !p.variants) return false;      /* unknown to the live store: not purchasable */
    return p.variants.some(function (v) { return v.available; });
  }

  /* The first PURCHASABLE variant on the live store, used only when the shopper never
     chose one (a control that adds at product level, like a cross-sell "Add"). */
  function firstVariant(id) {
    var p = VARIANTS && VARIANTS[id];
    if (!p || !p.variants || !p.variants.length) return null;
    var ok = p.variants.filter(function (v) { return v.available; });
    var v = ok[0] || null;
    return v ? { id: String(v.id), title: tidy(v.title), price: parseFloat(v.price) } : null;
  }

  /* Shopify cart permalink: SHOP/cart/<variantId>:<qty>,... It opens the REAL Shopify cart
     and checkout for these lines; nothing is charged until the shopper pays there. Returns
     null when any line cannot be resolved to a live variant, so the button can say so. */
  function checkoutUrl() {
    var its = items();
    if (!its.length || !VARIANTS) return null;   /* no snapshot, no checkout */
    var lines = [];
    for (var i = 0; i < its.length; i++) {
      var v = variantOf(its[i].key) || firstVariant(its[i].id);
      var p = VARIANTS[its[i].id];
      if (!v || !p || !p.variants) return null;   /* every product must exist on the live store */
      var live = p.variants.filter(function (x) { return String(x.id) === String(v.id); })[0];
      if (!live || !live.available) return null;  /* and every chosen variant must be live and available */
      lines.push(v.id + ":" + its[i].qty);
    }
    if (MIRROR && SYNCED) return SHOP + "/checkout";   /* the real cart holds exactly this bag */
    return SHOP + "/cart/" + lines.join(",");
  }

  /* Bags saved before 2026-09-08 hold product-only keys; once the snapshot is here each one
     becomes product@firstVariant and quantities merge, so no variant can sit on two lines. */
  function migrate() {
    if (!VARIANTS) return;
    var state = read(), chosen = readV(), changed = false;
    Object.keys(state).forEach(function (key) {
      if (key.indexOf("@") >= 0) {
        var lp = VARIANTS[pidOf(key)], lv = vidPart(key);   /* dead variant: drop the line, never crash */
        if (lp && lp.variants && lv && !lp.variants.some(function (x) { return String(x.id) === lv; })) {
          delete state[key]; delete chosen[key]; changed = true;
        }
        return;
      }
      var v = chosen[key] || firstVariant(key);
      if (!v || !v.id) return;
      var nk = key + "@" + v.id;
      state[nk] = (state[nk] || 0) + state[key]; delete state[key];
      chosen[nk] = chosen[nk] || v; delete chosen[key]; changed = true;
    });
    if (changed) { write(state); writeV(chosen); }
  }

  /* ---- grind (Koi, approved by Dion 2026-09-24) -------------------------------------------
     Kenya AA, Terroir and Experimental ask how the beans should be ground. The choice is part
     of the line: key "<id>@<variant>|<grind>", Shopify line-item property "Grind" with Koi's
     exact wording, so stock stays one number per coffee and the grind prints on the order. The
     Blend is pre-ground (medium) and has no choice. */
  var GRIND_PRODUCTS = { "kenya-aa": 1, "terroir": 1, "experimental": 1 };
  var GRINDS = [
    { code: "whole", head: "Whole Bean", detail: "Grind at home" },
    { code: "espresso", head: "Espresso / Moka Pot", detail: "Fine grind" },
    { code: "filter", head: "Filter / Pour Over / AeroPress", detail: "Medium grind, also suitable for V60 and drip/filter brewing" },
    { code: "press", head: "French Press / Cold Brew", detail: "Coarse grind" }
  ];
  function needsGrind(id) { return !!GRIND_PRODUCTS[pidOf(id)]; }
  function grindOf(key) { var k = String(key), i = k.indexOf("|"); return i >= 0 ? k.slice(i + 1) : null; }
  function vidPart(key) { var k = String(key).split("|")[0]; return k.indexOf("@") >= 0 ? k.split("@")[1] : null; }
  function grindInfo(code) { for (var i = 0; i < GRINDS.length; i++) if (GRINDS[i].code === code) return GRINDS[i]; return null; }
  function grindLabel(code) { var g = grindInfo(code); return g ? g.head + " · " + g.detail : String(code); }
  function grindHead(code) { var g = grindInfo(code); return g ? g.head : String(code); }
  function lineKey(id, vid, grind) { return id + (vid ? "@" + vid : "") + (grind ? "|" + grind : ""); }
  /* the store's current variant for a line, so the bag always shows today's price */
  function liveVariant(key) {
    var p = VARIANTS && VARIANTS[pidOf(key)], vid = vidPart(key);
    if (!p || !p.variants || !vid) return null;
    for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === vid) return p.variants[i];
    return null;
  }

  /* ---- the public surface ---------------------------------------------------- */

  function items() {
    migrate();
    var state = read();
    var chosen = readV();
    return Object.keys(state).map(function (key) {
      var id = pidOf(key);
      var p = product(id);
      var v = chosen[key] || null;
      /* a chosen variant overrides the catalogue's "from" price with its own live price */
      var lvar = liveVariant(key), g = grindOf(key);
      if (lvar) v = { id: String(lvar.id), title: tidy(lvar.title), price: parseFloat(lvar.price) };
      var price = v && typeof v.price === "number" ? v.price : (p ? p.price : null);
      return {
        id: id,
        key: key,
        qty: state[key],
        name: (p ? p.name : id) + (v && v.title && v.title !== "Default Title" ? " · " + v.title : "") + (g ? " · Grind: " + grindHead(g) : ""),
        grind: g ? grindLabel(g) : null,
        variant: v,
        price: price,
        priceNote: p ? p.priceNote : null,
        /* image paths in products.json are relative to the site root; resolved against
           BASE here so the drawer works on pages any number of folders deep. */
        image: p && (IMAGE_FALLBACK[id] || p.image) ? (BASE ? BASE + "/" + (IMAGE_FALLBACK[id] || p.image) : (IMAGE_FALLBACK[id] || p.image)) : null,
        category: p ? p.category : null,
        /* An unpriced line contributes nothing to the subtotal and says so on screen,
           rather than quietly counting as zero and making the total look wrong. */
        line: typeof price === "number" ? price * state[key] : null
      };
    });
  }

  function count() {
    var state = read(), n = 0;
    Object.keys(state).forEach(function (id) { n += state[id]; });
    return n;
  }

  function subtotal() {
    return items().reduce(function (sum, it) {
      return sum + (typeof it.line === "number" ? it.line : 0);
    }, 0);
  }

  function unpriced() {
    return items().filter(function (it) { return it.line === null; });
  }

  function add(id, qty) {
    var n = parseInt(qty, 10);
    if (!isFinite(n) || n < 1) n = 1;
    var state = read();
    state[id] = (state[id] || 0) + n;
    write(state);
    mirrorAdd(id, n);
    return state[id];
  }

  function setQuantity(id, qty) {
    var n = parseInt(qty, 10);
    var state = read();
    if (!isFinite(n) || n <= 0) delete state[id];   /* zero removes, never stores */
    else state[id] = n;
    write(state);
    mirrorSet(id);
    return state[id] || 0;
  }

  function remove(id) { return setQuantity(id, 0); }

  function clear() { write({}); mirrorClear(); }

  function subscribe(fn) {
    listeners.push(fn);
    /* Subscribers still get their first emit when the catalogue is down: the bag and its
       count are local state and must keep working. The handler is what stops a failed
       load from surfacing as an unhandled rejection, which root asked to see none of. */
    load().then(emit, function () { emit(); });
    return function () {
      listeners = listeners.filter(function (f) { return f !== fn; });
    };
  }

  function format(kes) {
    if (typeof kes !== "number") return "";
    return "KSh " + kes.toLocaleString("en-KE");
  }

  /* Two tabs open on the same store must not disagree about the cart. */
  global.addEventListener("storage", function (e) {
    if (e.key === KEY) emit();
  });

  /* THE STORE CART MIRROR (2026-09-23 tracking, 2026-09-24 grind). The bag in localStorage stays
     the display truth, and every add, quantity change and remove is ALSO sent to the store's
     real cart through Shopify's AJAX Cart API, so Shopify emits its standard events
     (product_added_to_cart, product_removed_from_cart) at the moment of adding, which the
     Facebook & Instagram app turns into AddToCart. A coffee's grind travels as the line-item
     property "Grind", so the same coffee in two grinds is two lines in both carts and the grind
     prints on the order. Only on a page served by the store itself: on the review site the
     origin differs and it stays off. Calls run one at a time and the real cart is read back
     after each; Checkout goes to /checkout once both carts hold exactly the same lines. */
  var MIRROR = SHOP === "" || !!(global.location && global.location.origin === SHOP);
  var SYNCED = false, mqueue = Promise.resolve();
  function vidOf(key) {
    var v = vidPart(key);
    if (v) return v;
    var f = variantOf(key) || firstVariant(pidOf(key));
    return f && f.id ? String(f.id) : null;
  }
  function propsOf(key) { var g = grindOf(key); return g ? { Grind: grindLabel(g) } : null; }
  function sig(vid, props) { return String(vid) + "|" + ((props && props.Grind) || ""); }
  function wanted() {
    var s = read(), out = {};
    Object.keys(s).forEach(function (k) {
      var v = vidOf(k); if (!v) return;
      var z = sig(v, propsOf(k)); out[z] = (out[z] || 0) + s[k];
    });
    return out;
  }
  function haveOf(c) {
    var have = {}, keys = {};
    (c.items || []).forEach(function (i) {
      var z = sig(i.variant_id, i.properties); have[z] = (have[z] || 0) + i.quantity; keys[z] = i.key;
    });
    return { have: have, keys: keys };
  }
  function lineItem(key, qty) {
    var it = { id: Number(vidOf(key)), quantity: qty }, pr = propsOf(key);
    if (pr) it.properties = pr;
    return it;
  }
  function cartApi(path, body) {
    return fetch(path, body ? { method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body) } : { credentials: "same-origin", headers: { "Accept": "application/json" } })
      .then(function (r) { if (!r.ok) throw new Error(path + " HTTP " + r.status); return r.json(); });
  }
  function readBack() {
    return cartApi("/cart.js").then(function (c) {
      var have = haveOf(c).have, want = wanted(), all = {};
      Object.keys(have).concat(Object.keys(want)).forEach(function (k) { all[k] = 1; });
      SYNCED = Object.keys(all).every(function (k) { return (have[k] || 0) === (want[k] || 0); });
      emit();
      return { have: have, want: want };
    });
  }
  function mirror(job) {
    if (!MIRROR) return mqueue;
    SYNCED = false;
    mqueue = mqueue.then(job).then(readBack).catch(function (e) {
      SYNCED = false;
      console.warn("[amka] store cart not updated:", e && e.message);
    });
    return mqueue;
  }
  function mirrorAdd(key, n) {
    mirror(function () { return vidOf(key) ? cartApi("/cart/add.js", { items: [lineItem(key, n)] }) : null; });
  }
  function mirrorSet(key) {
    mirror(function () {
      var v = vidOf(key); if (!v) return null;
      var z = sig(v, propsOf(key));
      return cartApi("/cart.js").then(function (c) {
        var h = haveOf(c), q = wanted()[z] || 0;
        if (h.keys[z]) return cartApi("/cart/change.js", { id: h.keys[z], quantity: q });
        return q > 0 ? cartApi("/cart/add.js", { items: [lineItem(key, q)] }) : null;
      });
    });
  }
  function mirrorClear() { mirror(function () { return cartApi("/cart/clear.js", {}); }); }
  /* On load: correct quantities and drop lines the bag no longer has, without announcing adds. */
  function reconcile() {
    return mirror(function () {
      return cartApi("/cart.js").then(function (c) {
        var h = haveOf(c), want = wanted(), upd = {}, diff = false;
        Object.keys(h.have).forEach(function (z) {
          var q = want[z] || 0;
          if (q !== h.have[z]) { upd[h.keys[z]] = q; diff = true; }
        });
        return diff ? cartApi("/cart/update.js", { updates: upd }) : null;
      });
    });
  }
  /* Checkout: first add anything the real cart is missing (a new session, an expired cart), grind
     included, then go to /checkout. Without the mirror (review site) the permalink is used. */
  function syncForCheckout() {
    if (!MIRROR) return Promise.resolve(checkoutUrl());
    return mirror(function () {
      return cartApi("/cart.js").then(function (c) {
        var h = haveOf(c), want = wanted(), upd = {}, adds = [], s = read(), seen = {};
        Object.keys(h.have).forEach(function (z) { var q = want[z] || 0; if (q !== h.have[z]) upd[h.keys[z]] = q; });
        Object.keys(s).forEach(function (k) {
          var v = vidOf(k); if (!v) return;
          var z = sig(v, propsOf(k));
          if (!h.have[z] && !seen[z]) { seen[z] = 1; adds.push(lineItem(k, want[z])); }
        });
        var first = Object.keys(upd).length ? cartApi("/cart/update.js", { updates: upd }) : Promise.resolve();
        return first.then(function () { return adds.length ? cartApi("/cart/add.js", { items: adds }) : null; });
      });
    }).then(function () { return SYNCED ? SHOP + "/checkout" : checkoutUrl(); });
  }
  if (MIRROR) loadVariants().then(function () { migrate(); return reconcile(); });

  global.AmkaCart = {
    BASE: BASE, DATA_URL: DATA_URL,
    load: load, product: product,
    items: items, count: count, subtotal: subtotal, unpriced: unpriced,
    add: add, setQuantity: setQuantity, remove: remove, clear: clear,
    subscribe: subscribe, format: format,
    setVariant: setVariant, variantOf: variantOf, firstVariant: firstVariant,
    purchasable: purchasable, pidOf: pidOf,
    checkoutUrl: checkoutUrl, loadVariants: loadVariants, SHOP: SHOP,
    KEY: KEY, VKEY: VKEY,
    storeCart: function () { return { mirror: MIRROR, synced: SYNCED }; }, syncStoreCart: reconcile,
    GRINDS: GRINDS, needsGrind: needsGrind, grindOf: grindOf, grindLabel: grindLabel, grindHead: grindHead,
    lineKey: lineKey, liveVariant: liveVariant, syncForCheckout: syncForCheckout
  };
})(window);
